import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Grid,
    Typography,
    Avatar,
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    LinearProgress,
    ButtonBase,
    useTheme,
} from '@mui/material';
import {
    CheckCircleRounded,
    RadioButtonUncheckedRounded,
    AutoFixHigh as AutoFixHighIcon,
    Tour as TourIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/sk';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';
import Chart from 'react-apexcharts';

import dashboardService from '../services/dashboard.service';
import tasksService from '../services/tasks.service';
import employeeService from '../services/employee.service';
import AuthService from '../services/auth.service';
import { fireConfetti } from './onboarding/confetti';
import { getOnboardingState, updateOnboardingState, requestWizard, requestTour } from './onboarding/onboardingStorage';
import { usePreferences } from '../contexts/PreferencesContext';

import {
    DASH,
    EVENT_TONES,
    resolvePhotoUrl,
    getInitials,
    avatarTint,
} from './dashboard/dashboardTokens';
import { Panel, PanelHead, PanelBody, Eyebrow, MetricCell, EmptyState, BlockSkeleton, RowSkeleton } from './dashboard/Surface';
import SegmentedToggle from './dashboard/SegmentedToggle';
import {
    renderAttendanceTooltip,
    tooltipPalette,
    tooltipContainerSx,
    MAX_TOOLTIP_ROWS,
} from './dashboard/chartTooltip';
import PresenceHero from './dashboard/PresenceHero';
import ActivityFeed from './dashboard/ActivityFeed';
import TasksOverview from './dashboard/TasksOverview';

const DAYJS_LOCALE = { SK: 'sk', EN: 'en', CZ: 'cs' };

const ACTIVITY_LIMIT = 20;
// Nástenka ukazuje posledné dianie, nie zoznam úloh — na to je appka.
const TASKS_LIMIT = 6;
const REFRESH_MS = 60000;

const formatChartDayLabel = (dateStr, language) => {
    const locale = DAYJS_LOCALE[language] || 'sk';
    const label = dayjs(dateStr, 'YYYY-MM-DD').locale(locale).format('ddd D.M.');
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatChartDayTooltip = (dateStr, language) => {
    const locale = DAYJS_LOCALE[language] || 'sk';
    return dayjs(dateStr, 'YYYY-MM-DD').locale(locale).format('dddd D. MMMM');
};

const fillWeeklyTrend = (weeklyTrend, endDate) => {
    const map = new Map((weeklyTrend || []).map((d) => [d.date, d.present]));
    const days = [];
    for (let i = 6; i >= 0; i -= 1) {
        const d = dayjs(endDate, 'YYYY-MM-DD').subtract(i, 'day').format('YYYY-MM-DD');
        days.push({ date: d, present: map.get(d) || 0 });
    }
    return days;
};

const fillHourSeries = (rows) => {
    const map = new Map((rows || []).map((h) => [h.hour, h.count]));
    return Array.from({ length: 24 }, (_, hour) => map.get(hour) || 0);
};

const formatDayChipLabel = (dateStr, language) => {
    const locale = DAYJS_LOCALE[language] || 'sk';
    const label = dayjs(dateStr, 'YYYY-MM-DD').locale(locale).format('dd D.M.');
    return label.charAt(0).toUpperCase() + label.slice(1);
};

// "8h 42m" -> 522 (minutes) for sorting; null-safe.
const parseHmToMinutes = (str) => {
    if (!str) return 0;
    const h = /(\d+)\s*h/.exec(str);
    const m = /(\d+)\s*m/.exec(str);
    return (h ? Number(h[1]) : 0) * 60 + (m ? Number(m[1]) : 0);
};

// "08:42" -> 522 (minutes since midnight) for sorting; empty sorts last.
const parseClockToMinutes = (str) => {
    if (!str) return Number.POSITIVE_INFINITY;
    const match = /^(\d{1,2}):(\d{2})/.exec(str);
    if (!match) return Number.POSITIVE_INFINITY;
    return Number(match[1]) * 60 + Number(match[2]);
};

const ANALYTICS_PERIODS = [
    { value: 7, labelKey: 'period7' },
    { value: 14, labelKey: 'period14' },
    { value: 30, labelKey: 'period30' },
    { value: 90, labelKey: 'period90' },
    { value: 365, labelKey: 'period365' },
];

const CHART_VIEWS = [
    { value: 'weekly', labelKey: 'weekly' },
    { value: 'daily', labelKey: 'byDay' },
];

/**
 * Priznanie pod priemermi: ktoré smeny sa do nich nerátali a prečo.
 *
 * Vylúčiť dáta a nepovedať to je horšie než ich nechať vnútri — preto je
 * zoznam rozklikávateľný a každý riadok vedie na deň, kde sa dá smena opraviť.
 */
const AutoEndedNote = ({ autoEnded, language, t }) => {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const locale = DAYJS_LOCALE[language] || 'sk';
    const count = autoEnded?.count || 0;
    if (!count) return null;

    const shortDate = (value) => dayjs(value, 'YYYY-MM-DD').locale(locale).format('D. M.');

    return (
        <Box sx={{ mt: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: EVENT_TONES.break_start, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">
                    {t('pages.dashboard.period.autoEndedNote').replace('{count}', String(count))}
                </Typography>
                {autoEnded.shifts?.length > 0 && (
                    <Button
                        size="small"
                        onClick={() => setOpen((prev) => !prev)}
                        sx={{ minWidth: 0, p: 0, fontSize: '0.75rem', color: 'primary.main', '&:hover': { bgcolor: 'transparent' } }}
                    >
                        {t(open ? 'pages.dashboard.period.autoEndedHide' : 'pages.dashboard.period.autoEndedShow')}
                    </Button>
                )}
            </Stack>

            {open && (
                <Box sx={{ mt: 1.25 }}>
                    {autoEnded.shifts.map((shift) => (
                        <ButtonBase
                            key={`${shift.date}-${shift.employeeName}-${shift.startTime}`}
                            onClick={() => navigate(shift.employeeId
                                ? `/employees/${shift.employeeId}?date=${shift.date}`
                                : `/employees?date=${shift.date}`)}
                            sx={{
                                width: '100%',
                                justifyContent: 'space-between',
                                gap: 2,
                                py: 0.875,
                                px: 1,
                                mx: -1,
                                borderRadius: `${DASH.radiusXs}px`,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:hover': { bgcolor: 'action.hover' },
                                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 550 }} noWrap>
                                {shift.employeeName}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ whiteSpace: 'nowrap', ...DASH.numeric }}
                            >
                                {`${shortDate(shift.date)} ${shift.startTime} → ${shortDate(shift.endDate)} ${shift.endTime}`}
                            </Typography>
                        </ButtonBase>
                    ))}
                </Box>
            )}
        </Box>
    );
};

// ---- Comparative employee scoreboard ----
const SCOREBOARD_COLUMNS = [
    { key: 'name', labelKey: 'colEmployee', align: 'left', sort: (r) => r.name.toLowerCase() },
    { key: 'hours', labelKey: 'colHours', align: 'right', sort: (r) => parseHmToMinutes(r.totalNetWorkStr) },
    { key: 'days', labelKey: 'colDays', align: 'right', sort: (r) => r.daysWorked },
    { key: 'arrival', labelKey: 'colArrival', align: 'right', sort: (r) => parseClockToMinutes(r.avgArrivalTime) },
    { key: 'departure', labelKey: 'colDeparture', align: 'right', sort: (r) => parseClockToMinutes(r.avgDepartureTime) },
    { key: 'break', labelKey: 'colBreak', align: 'right', sort: (r) => parseHmToMinutes(r.avgBreakStr) },
];

const EmployeeScoreboard = ({ rows, t }) => {
    const navigate = useNavigate();
    const [orderBy, setOrderBy] = useState('hours');
    const [order, setOrder] = useState('desc');

    const handleSort = (key) => {
        if (orderBy === key) {
            setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setOrderBy(key);
            setOrder(key === 'name' ? 'asc' : 'desc');
        }
    };

    const sorted = useMemo(() => {
        const col = SCOREBOARD_COLUMNS.find((c) => c.key === orderBy) || SCOREBOARD_COLUMNS[1];
        const dir = order === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const va = col.sort(a);
            const vb = col.sort(b);
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });
    }, [rows, orderBy, order]);

    return (
        <TableContainer sx={{ mx: -1 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {SCOREBOARD_COLUMNS.map((col, idx) => (
                            <TableCell
                                key={col.key}
                                align={col.align}
                                sortDirection={orderBy === col.key ? order : false}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.6875rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.09em',
                                    color: 'text.secondary',
                                    borderBottomColor: 'divider',
                                    whiteSpace: 'nowrap',
                                    py: 1,
                                    pl: idx === 0 ? 1 : undefined,
                                }}
                            >
                                <TableSortLabel
                                    active={orderBy === col.key}
                                    direction={orderBy === col.key ? order : 'asc'}
                                    onClick={() => handleSort(col.key)}
                                >
                                    {t(`pages.dashboard.scoreboard.${col.labelKey}`)}
                                </TableSortLabel>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sorted.map((r) => (
                        <TableRow
                            key={r.id}
                            hover
                            onClick={() => navigate(`/employees/${r.id}`)}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.hover' },
                                '& td': { borderBottomColor: 'divider', py: 1.125, ...DASH.numeric },
                                '&:last-of-type td': { borderBottom: 'none' },
                            }}
                        >
                            <TableCell sx={{ pl: 1 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar
                                        src={r.photo || undefined}
                                        alt={r.name}
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            flexShrink: 0,
                                            bgcolor: avatarTint(r.name),
                                            color: '#fff',
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {getInitials(r.name)}
                                    </Avatar>
                                    <Typography variant="body2" sx={{ fontWeight: 550 }} noWrap>
                                        {r.name}
                                    </Typography>
                                </Stack>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {r.totalNetWorkStr || '–'}
                            </TableCell>
                            <TableCell align="right">{r.daysWorked || 0}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{r.avgArrivalTime || '–'}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{r.avgDepartureTime || '–'}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{r.avgBreakStr || '–'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const formatAnalyticsDate = (dateStr, language) => {
    if (!dateStr) return '';
    const locale = DAYJS_LOCALE[language] || 'sk';
    return dayjs(dateStr, 'YYYY-MM-DD').locale(locale).format('D. M. YYYY');
};

const BoardUser = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t, language } = usePreferences();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [analyticsPeriod, setAnalyticsPeriod] = useState(30);
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    const [chartView, setChartView] = useState('weekly');
    const [selectedDay, setSelectedDay] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [insights, setInsights] = useState(null);
    const [insightsLoading, setInsightsLoading] = useState(true);
    const [activity, setActivity] = useState(null);
    const [activityLoading, setActivityLoading] = useState(true);
    const [tasksOverview, setTasksOverview] = useState(null);
    const [tasksLoading, setTasksLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Prvé načítanie ukáže kostry; obnova na pozadí nechá stránku stáť na mieste.
    const loadedOnce = useRef(false);

    const fetchLive = useCallback((silent = false) => {
        if (!silent) {
            setLoading(true);
            setActivityLoading(true);
            setTasksLoading(true);
        }
        const today = dayjs().format('YYYY-MM-DD');
        return Promise.allSettled([
            dashboardService.getStats(today).then((res) => setStats(res.data)),
            dashboardService.getActivity(ACTIVITY_LIMIT).then((res) => setActivity(res.data)),
            // Úlohy žijú na chat-serveri — keď je nedostupný, zvyšok nástenky
            // to nesmie zhodiť, preto samostatný sľub v allSettled.
            tasksService.getTasksOverview(TASKS_LIMIT).then((res) => setTasksOverview(res.data)),
        ]).then((results) => {
            results.forEach((r) => {
                if (r.status === 'rejected') console.error('Error fetching dashboard live data', r.reason);
            });
            loadedOnce.current = true;
            setLoading(false);
            setActivityLoading(false);
            setTasksLoading(false);
            setLastUpdated(dayjs());
        });
    }, []);

    useEffect(() => {
        fetchLive();
    }, [fetchLive]);

    // Nástenka má byť živá — obnovujeme len keď je karta naozaj na obrazovke.
    useEffect(() => {
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') fetchLive(true);
        }, REFRESH_MS);
        return () => clearInterval(id);
    }, [fetchLive]);

    useEffect(() => {
        employeeService.getEmployees()
            .then((res) => setEmployees(Array.isArray(res.data) ? res.data.filter((e) => !e.is_ai_assistant) : []))
            .catch((err) => console.error('Error fetching employees for filter', err));
    }, []);

    const fetchAnalytics = useCallback(() => {
        setAnalyticsLoading(true);
        dashboardService.getAnalytics(analyticsPeriod)
            .then((res) => setAnalytics(res.data))
            .catch((err) => console.error('Error fetching dashboard analytics', err))
            .finally(() => setAnalyticsLoading(false));
    }, [analyticsPeriod]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    useEffect(() => {
        setInsightsLoading(true);
        employeeService.getInsights(analyticsPeriod)
            .then((res) => setInsights(res.data))
            .catch((err) => console.error('Error fetching employee insights', err))
            .finally(() => setInsightsLoading(false));
    }, [analyticsPeriod]);

    const scoreboardRows = useMemo(() => {
        const map = insights?.byEmployeeId || {};
        return employees
            .map((e) => {
                const avg = map[String(e.id)]?.averages;
                if (!avg || !avg.hasData) return null;
                return {
                    id: e.id,
                    name: e.name,
                    photo: resolvePhotoUrl(e.profile_photo_url),
                    totalNetWorkStr: avg.totalNetWorkStr,
                    daysWorked: avg.daysWorked,
                    avgArrivalTime: avg.avgArrivalTime,
                    avgDepartureTime: avg.avgDepartureTime,
                    avgBreakStr: avg.avgBreakStr,
                };
            })
            .filter(Boolean);
    }, [insights, employees]);
    const hasScoreboard = scoreboardRows.length > 0;

    const analyticsPeriodOptions = useMemo(
        () => ANALYTICS_PERIODS.map(({ value, labelKey }) => ({
            value,
            label: t(`pages.dashboard.analytics.${labelKey}`),
        })),
        [t]
    );

    const chartViewOptions = useMemo(
        () => CHART_VIEWS.map(({ value, labelKey }) => ({
            value,
            label: t(`pages.dashboard.charts.${labelKey}`),
        })),
        [t]
    );

    const weeklyTrendFilled = useMemo(
        () => fillWeeklyTrend(stats?.weeklyTrend, stats?.date || dayjs().format('YYYY-MM-DD')),
        [stats]
    );

    const dayOptions = useMemo(
        () => weeklyTrendFilled.map((d) => ({
            value: d.date,
            label: formatDayChipLabel(d.date, language),
        })),
        [weeklyTrendFilled, language]
    );

    const activeDay = useMemo(() => {
        const available = dayOptions.map((d) => d.value);
        if (selectedDay && available.includes(selectedDay)) return selectedDay;
        return available[available.length - 1] || (stats?.date || dayjs().format('YYYY-MM-DD'));
    }, [selectedDay, dayOptions, stats]);

    const dayActivityFilled = useMemo(() => {
        const entry = (stats?.dayActivity || []).find((d) => d.date === activeDay);
        return {
            arrivals: fillHourSeries(entry?.arrivals),
            departures: fillHourSeries(entry?.departures),
        };
    }, [stats, activeDay]);

    // Smeny za posledných 7 dní, indexované dňom príchodu — z nich sa skladá
    // menný rozpis v tooltipe grafu.
    const weekShifts = useMemo(() => stats?.weekShifts || [], [stats?.weekShifts]);
    const hasWeekShifts = !stats?.weekShiftsTruncated && weekShifts.length > 0;

    // Kto v daný deň prišiel, kedy prvýkrát a ako sa mu smena skončila.
    // Kľúčom je deň príchodu, nie deň zápisu odchodu — smena, ktorú automatizácia
    // ukončila až ráno na druhý deň, tak ostáva pri dni, do ktorého patrí.
    // Dĺžka zoznamu presne zodpovedá výške stĺpca (ten počíta príchody).
    const rosterForDate = useCallback((date) => {
        const byEmployee = new Map();
        weekShifts.forEach((shift) => {
            if (shift.startDate !== date) return;
            if (!byEmployee.has(shift.employeeId)) byEmployee.set(shift.employeeId, []);
            byEmployee.get(shift.employeeId).push(shift);
        });
        return Array.from(byEmployee.values())
            .map((shifts) => {
                const sorted = [...shifts].sort((a, b) => a.startTime.localeCompare(b.startTime));
                const closing = sorted[sorted.length - 1];
                return {
                    name: sorted[0].name,
                    firstIn: sorted[0].startTime,
                    lastOut: closing.endTime,
                    endDayOffset: closing.endDayOffset,
                    autoEnded: closing.autoEnded,
                };
            })
            .sort((a, b) => a.firstIn.localeCompare(b.firstIn));
    }, [weekShifts]);

    const dailyHasData = useMemo(
        () => dayActivityFilled.arrivals.some((v) => v > 0) || dayActivityFilled.departures.some((v) => v > 0),
        [dayActivityFilled]
    );

    const navigateToEmployeesForDate = useCallback((dateStr) => {
        if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return;
        navigate(`/employees?date=${dateStr}`);
    }, [navigate]);

    const handleChartClick = useCallback((_event, _chartContext, config) => {
        const idx = config?.dataPointIndex;
        if (idx == null || idx < 0) return;
        if (chartView === 'weekly') {
            const day = weeklyTrendFilled[idx]?.date;
            if (day) navigateToEmployeesForDate(day);
            return;
        }
        navigateToEmployeesForDate(activeDay);
    }, [chartView, weeklyTrendFilled, activeDay, navigateToEmployeesForDate]);

    const chartOptions = useMemo(() => {
        const isDark = theme.palette.mode === 'dark';
        const textColor = isDark ? '#94a3b8' : '#64748B';
        const gridColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.07)';
        const palette = tooltipPalette(theme);
        const today = dayjs().format('YYYY-MM-DD');

        const moreLabelFor = (rest) => (rest > 0
            ? t('pages.dashboard.charts.tooltip.more').replace('{count}', String(rest))
            : null);

        // Koniec smeny tak, ako sa naozaj stal: čas, prípadne „+1 d“, keď sa
        // zapísal na druhý deň, a oranžová značka, ak ho zapísal systém.
        const endDetail = (row, date) => {
            if (!row.lastOut) {
                return date === today
                    ? { live: true, text: t('pages.dashboard.charts.tooltip.stillAtWork') }
                    : { text: '–' };
            }
            const offset = row.endDayOffset > 0 ? ` +${row.endDayOffset} d` : '';
            return { warn: row.autoEnded, text: `${row.lastOut}${offset}` };
        };

        // Menný rozpis dňa: kto prišiel, o koľkej a ako sa mu smena skončila.
        const weeklyTooltip = ({ dataPointIndex }) => {
            const date = weeklyTrendFilled[dataPointIndex]?.date;
            const roster = date ? rosterForDate(date) : [];
            const shown = roster.slice(0, MAX_TOOLTIP_ROWS);
            return renderAttendanceTooltip({
                palette,
                title: formatChartDayTooltip(date, language),
                summary: t('pages.dashboard.charts.tooltip.presentCount')
                    .replace('{count}', String(roster.length)),
                rows: shown.map((row) => {
                    const end = endDetail(row, date);
                    return {
                        name: row.name,
                        live: end.live,
                        warn: end.warn,
                        detail: `${row.firstIn} → ${end.text}`,
                    };
                }),
                moreLabel: moreLabelFor(roster.length - shown.length),
                footnote: shown.some((row) => row.autoEnded)
                    ? t('pages.dashboard.charts.tooltip.autoEndedNote')
                    : null,
            });
        };

        // Rovnaká hodina, ale len jedna strana pohybu — príchody alebo odchody.
        const hourlyTooltip = ({ seriesIndex, dataPointIndex }) => {
            const arrivals = seriesIndex === 0;
            const matches = weekShifts
                .filter((shift) => (arrivals
                    ? shift.startDate === activeDay && shift.startHour === dataPointIndex
                    : shift.endDate === activeDay && shift.endHour === dataPointIndex))
                .sort((a, b) => (arrivals ? a.startTime.localeCompare(b.startTime) : a.endTime.localeCompare(b.endTime)));
            const shown = matches.slice(0, MAX_TOOLTIP_ROWS);
            const hour = String(dataPointIndex).padStart(2, '0');
            return renderAttendanceTooltip({
                palette,
                title: `${hour}:00 – ${hour}:59`,
                summary: `${arrivals
                    ? t('pages.dashboard.charts.arrivals')
                    : t('pages.dashboard.charts.departures')} · ${matches.length}`,
                rows: shown.map((shift) => ({
                    name: shift.name,
                    warn: !arrivals && shift.autoEnded,
                    detail: arrivals ? shift.startTime : shift.endTime,
                })),
                moreLabel: moreLabelFor(matches.length - shown.length),
                footnote: !arrivals && shown.some((shift) => shift.autoEnded)
                    ? t('pages.dashboard.charts.tooltip.autoEndedNote')
                    : null,
            });
        };

        const base = {
            chart: {
                toolbar: { show: false },
                fontFamily: 'inherit',
                events: { click: handleChartClick },
                animations: { speed: 400 },
            },
            dataLabels: { enabled: false },
            grid: {
                borderColor: gridColor,
                strokeDashArray: 0,
                xaxis: { lines: { show: false } },
                padding: { left: 4, right: 4, top: 0 },
            },
            states: { hover: { filter: { type: 'darken', value: 0.9 } } },
        };

        if (chartView === 'weekly') {
            return {
                ...base,
                plotOptions: { bar: { borderRadius: 7, borderRadiusApplication: 'end', columnWidth: '42%' } },
                xaxis: {
                    categories: weeklyTrendFilled.map((d) => formatChartDayLabel(d.date, language)),
                    labels: { style: { colors: textColor, fontSize: '12px' } },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                },
                yaxis: {
                    labels: { style: { colors: textColor, fontSize: '12px' } },
                    min: 0,
                    forceNiceScale: true,
                },
                colors: [theme.palette.primary.main],
                tooltip: {
                    theme: isDark ? 'dark' : 'light',
                    ...(hasWeekShifts
                        ? { custom: weeklyTooltip }
                        : {
                            x: {
                                formatter: (_, { dataPointIndex }) => (
                                    formatChartDayTooltip(weeklyTrendFilled[dataPointIndex]?.date, language)
                                ),
                            },
                        }),
                },
            };
        }

        return {
            ...base,
            plotOptions: { bar: { borderRadius: 4, borderRadiusApplication: 'end', columnWidth: '70%' } },
            xaxis: {
                type: 'category',
                categories: Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`),
                labels: {
                    style: { colors: textColor, fontSize: '10px' },
                    rotate: -45,
                    hideOverlappingLabels: true,
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
            yaxis: {
                labels: { style: { colors: textColor, fontSize: '12px' } },
                min: 0,
                forceNiceScale: true,
            },
            legend: {
                position: 'top',
                horizontalAlign: 'right',
                fontSize: '12px',
                labels: { colors: textColor },
                markers: { size: 5, offsetX: -3 },
                itemMargin: { horizontal: 10 },
            },
            colors: [EVENT_TONES.arrival, EVENT_TONES.departure],
            tooltip: {
                theme: isDark ? 'dark' : 'light',
                ...(hasWeekShifts
                    ? { custom: hourlyTooltip }
                    : {
                        x: {
                            formatter: (_, { dataPointIndex }) => `${String(dataPointIndex).padStart(2, '0')}:00`,
                        },
                    }),
            },
        };
    }, [
        chartView,
        theme,
        weeklyTrendFilled,
        language,
        handleChartClick,
        hasWeekShifts,
        rosterForDate,
        weekShifts,
        activeDay,
        t,
    ]);

    const chartSeries = useMemo(() => {
        if (chartView === 'weekly') {
            return [{ name: t('pages.dashboard.charts.present'), data: weeklyTrendFilled.map((d) => d.present) }];
        }
        return [
            { name: t('pages.dashboard.charts.arrivals'), data: dayActivityFilled.arrivals },
            { name: t('pages.dashboard.charts.departures'), data: dayActivityFilled.departures },
        ];
    }, [chartView, weeklyTrendFilled, dayActivityFilled, t]);


    const hasAnalyticsData = Boolean(
        analytics?.averages?.avgArrivalTime
        || (analytics?.topEmployees?.byHours?.length > 0)
        || (analytics?.topEmployees?.byDays?.length > 0)
        || analytics?.punctuality?.arrivalConsistencyMin != null
    );

    const analyticsPeriodLabel = useMemo(() => {
        if (!analytics?.rangeFrom || !analytics?.rangeTo) return '';
        return t('pages.dashboard.analytics.periodRange')
            .replace('{from}', formatAnalyticsDate(analytics.rangeFrom, language))
            .replace('{to}', formatAnalyticsDate(analytics.rangeTo, language));
    }, [analytics, language, t]);

    // Oslava prvého príchodu — confetti raz, len pre firmy, ktoré prešli onboardingom
    useEffect(() => {
        const user = AuthService.getCurrentUser();
        if (!user?.id) return;
        const onboarding = getOnboardingState(user.id);
        const wasOnboarded = onboarding.welcomeDismissed || onboarding.wizardCompleted || onboarding.wizardSkipped;
        if (!wasOnboarded || onboarding.celebrated) return;
        const employeesOk = (stats?.kpis?.totalEmployees || 0) > 0;
        const workplacesOk = (stats?.totalWorkplaces || 0) > 0;
        if (employeesOk && workplacesOk && hasAnalyticsData) {
            updateOnboardingState(user.id, { celebrated: true });
            fireConfetti();
        }
    }, [stats, hasAnalyticsData]);

    const kpis = stats?.kpis || {};
    const hasEmployees = (kpis.totalEmployees || 0) > 0;
    const hasWorkplaces = (stats?.totalWorkplaces || 0) > 0;
    const showChecklist = !hasEmployees || !hasWorkplaces || !hasAnalyticsData;

    const averageTiles = useMemo(() => {
        const averages = analytics?.averages || {};
        const tiles = [
            {
                key: 'arrival',
                label: t('pages.dashboard.analytics.avgArrival'),
                value: averages.avgArrivalTime,
                hint: t('pages.dashboard.analytics.avgArrivalHint'),
            },
            {
                key: 'departure',
                label: t('pages.dashboard.analytics.avgDeparture'),
                value: averages.avgDepartureTime,
                hint: t('pages.dashboard.analytics.avgDepartureHint'),
            },
            {
                key: 'work',
                label: t('pages.dashboard.analytics.avgWork'),
                value: averages.avgNetWorkStr,
                hint: t('pages.dashboard.analytics.avgWorkHint'),
            },
            {
                key: 'break',
                label: t('pages.dashboard.analytics.avgBreak'),
                value: averages.avgBreakStr,
                hint: t('pages.dashboard.analytics.avgBreakHint'),
            },
        ];
        if (analytics?.punctuality?.arrivalConsistencyMin != null) {
            tiles.push({
                key: 'consistency',
                label: t('pages.dashboard.analytics.consistency'),
                value: `${analytics.punctuality.arrivalConsistencyMin} ${t('pages.dashboard.analytics.consistencyUnit')}`,
                hint: t('pages.dashboard.analytics.consistencyHint'),
            });
        }
        return tiles;
    }, [analytics, t]);

    const renderChecklist = () => {
        const firstCheckinDone = hasEmployees && hasWorkplaces && hasAnalyticsData;
        const steps = [
            {
                key: 'workplace',
                done: hasWorkplaces,
                title: t('pages.dashboard.onboarding.step1Title'),
                desc: t('pages.dashboard.onboarding.step1Desc'),
                buttonText: t('pages.dashboard.onboarding.step1Btn'),
                link: '/workplaces',
            },
            {
                key: 'qr',
                done: firstCheckinDone,
                title: t('pages.dashboard.onboarding.step2Title'),
                desc: t('pages.dashboard.onboarding.step2Desc'),
                buttonText: t('pages.dashboard.onboarding.step2Btn'),
                link: '/workplaces',
                disabled: !hasWorkplaces,
            },
            {
                key: 'employees',
                done: hasEmployees,
                title: t('pages.dashboard.onboarding.step3Title'),
                desc: t('pages.dashboard.onboarding.step3Desc'),
                buttonText: t('pages.dashboard.onboarding.step3Btn'),
                link: '/employees',
            },
            {
                key: 'firstCheckin',
                done: firstCheckinDone,
                title: t('pages.dashboard.onboarding.step4Title'),
                desc: t('pages.dashboard.onboarding.step4Desc'),
            },
        ];
        const doneCount = steps.filter((s) => s.done).length;

        return (
            <Panel sx={{ mb: DASH.gap }}>
                <PanelHead
                    title={t('pages.dashboard.onboarding.title')}
                    meta={t('pages.dashboard.onboarding.subtitle')
                        .replace('{done}', doneCount)
                        .replace('{total}', steps.length)}
                    sx={{ mb: 2 }}
                />
                <LinearProgress
                    variant="determinate"
                    value={(doneCount / steps.length) * 100}
                    sx={{
                        mb: 2.5,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': { borderRadius: 3, transition: 'transform .8s ease' },
                    }}
                />
                <Stack spacing={2}>
                    {steps.map((step, idx) => (
                        <Stack
                            key={step.key}
                            direction="row"
                            spacing={1.75}
                            alignItems="flex-start"
                            sx={{ opacity: step.disabled ? 0.45 : 1 }}
                        >
                            {step.done
                                ? <CheckCircleRounded sx={{ color: EVENT_TONES.arrival, mt: 0.125, fontSize: 20 }} />
                                : <RadioButtonUncheckedRounded sx={{ color: 'text.disabled', mt: 0.125, fontSize: 20 }} />}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 600,
                                        color: step.done ? 'text.disabled' : 'text.primary',
                                        textDecoration: step.done ? 'line-through' : 'none',
                                    }}
                                >
                                    {idx + 1}. {step.title}
                                </Typography>
                                {!step.done && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                        {step.desc}
                                    </Typography>
                                )}
                            </Box>
                            {!step.done && step.buttonText && (
                                <Button
                                    component={RouterLink}
                                    to={step.link}
                                    size="small"
                                    variant="outlined"
                                    disabled={step.disabled}
                                    sx={{ borderRadius: '999px', whiteSpace: 'nowrap', flexShrink: 0, px: 2 }}
                                >
                                    {step.buttonText}
                                </Button>
                            )}
                        </Stack>
                    ))}
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                        size="small"
                        startIcon={<AutoFixHighIcon fontSize="small" />}
                        onClick={requestWizard}
                        sx={{ borderRadius: '999px', color: 'text.secondary' }}
                    >
                        {t('onboardingFlow.checklist.runWizard')}
                    </Button>
                    <Button
                        size="small"
                        startIcon={<TourIcon fontSize="small" />}
                        onClick={requestTour}
                        sx={{ borderRadius: '999px', color: 'text.secondary', display: { xs: 'none', md: 'inline-flex' } }}
                    >
                        {t('onboardingFlow.checklist.runTour')}
                    </Button>
                </Stack>
            </Panel>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
            {showChecklist && renderChecklist()}

            {/* Teraz: prítomnosť + nástenka */}
            <Grid container spacing={DASH.gap} sx={{ mb: DASH.gap, alignItems: 'stretch' }}>
                <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
                    <PresenceHero
                        kpis={kpis}
                        totalWorkplaces={stats?.totalWorkplaces}
                        byWorkplace={stats?.byWorkplace}
                        present={activity?.present || []}
                        loading={loading && !loadedOnce.current}
                        t={t}
                        language={language}
                    />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
                    <Panel>
                        <PanelHead
                            title={t('pages.dashboard.feed.title')}
                            meta={lastUpdated
                                ? t('pages.dashboard.feed.updatedAt').replace('{time}', lastUpdated.format('H:mm'))
                                : t('pages.dashboard.feed.subtitle')}
                            sx={{ mb: 1 }}
                        />
                        <ActivityFeed
                            events={activity?.events || []}
                            loading={activityLoading && !loadedOnce.current}
                            language={language}
                            t={t}
                        />
                    </Panel>
                </Grid>
            </Grid>

            {/* Dochádzka v čase */}
            <Panel sx={{ mb: DASH.gap }}>
                <PanelHead
                    title={chartView === 'weekly'
                        ? t('pages.dashboard.charts.weeklyTrend')
                        : t('pages.dashboard.charts.dayDetail')}
                    meta={chartView === 'weekly' ? t('pages.dashboard.charts.clickHint') : undefined}
                    actions={(
                        <SegmentedToggle
                            value={chartView}
                            onChange={setChartView}
                            options={chartViewOptions}
                            size="sm"
                        />
                    )}
                />
                {chartView === 'daily' && (
                    <Box sx={{ mb: 2 }}>
                        <SegmentedToggle
                            value={activeDay}
                            onChange={setSelectedDay}
                            options={dayOptions}
                            size="sm"
                        />
                    </Box>
                )}
                <PanelBody>
                    {loading && !loadedOnce.current ? (
                        <BlockSkeleton height={DASH.chartHeight} />
                    ) : chartView === 'daily' && !dailyHasData ? (
                        <EmptyState
                            title={t('pages.dashboard.charts.noDayData')}
                            hint={t('pages.dashboard.charts.noDayDataHint')}
                        />
                    ) : (
                        <Box sx={(th) => ({
                            '& .apexcharts-bar-area': { cursor: 'pointer' },
                            ...tooltipContainerSx(th),
                        })}>
                            <Chart
                                key={chartView}
                                options={chartOptions}
                                series={chartSeries}
                                type="bar"
                                height={DASH.chartHeight}
                            />
                        </Box>
                    )}
                </PanelBody>
            </Panel>

            {/* Zvolené obdobie: najprv priemer tímu, potom rozpad po ľuďoch.
                Jeden prepínač na paneli, ktorý naozaj ovláda — nie v hlavičke stránky. */}
            <Panel sx={{ mb: DASH.gap }}>
                <PanelHead
                    title={t('pages.dashboard.period.title')}
                    meta={analyticsPeriodLabel}
                    actions={(
                        <SegmentedToggle
                            value={analyticsPeriod}
                            onChange={setAnalyticsPeriod}
                            options={analyticsPeriodOptions}
                            size="sm"
                        />
                    )}
                />
                <PanelBody>
                    <Eyebrow sx={{ mb: 1.5 }}>{t('pages.dashboard.period.teamLabel')}</Eyebrow>
                    {analyticsLoading ? (
                        <BlockSkeleton height={72} />
                    ) : !hasAnalyticsData ? (
                        <EmptyState
                            title={t('pages.dashboard.analytics.noData')}
                            hint={t('pages.dashboard.analytics.noDataHint')}
                            sx={{ py: 2 }}
                        />
                    ) : (
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            sx={{ flexWrap: 'wrap' }}
                            useFlexGap
                        >
                            {averageTiles.map((tile, idx) => (
                                <MetricCell
                                    key={tile.key}
                                    label={tile.label}
                                    value={tile.value}
                                    hint={tile.hint}
                                    isFirst={idx === 0}
                                />
                            ))}
                        </Stack>
                    )}

                    <AutoEndedNote autoEnded={analytics?.autoEnded} language={language} t={t} />

                    <Box sx={{ mt: 3.5, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Eyebrow sx={{ display: 'block', mb: 0.5 }}>
                            {t('pages.dashboard.period.byEmployeeLabel')}
                        </Eyebrow>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            {t('pages.dashboard.scoreboard.subtitle')}
                        </Typography>
                        {insightsLoading ? (
                            <RowSkeleton rows={5} />
                        ) : !hasScoreboard ? (
                            <EmptyState
                                title={t('pages.dashboard.scoreboard.noData')}
                                hint={t('pages.dashboard.scoreboard.noDataHint')}
                            />
                        ) : (
                            <EmployeeScoreboard rows={scoreboardRows} t={t} />
                        )}
                    </Box>
                </PanelBody>
            </Panel>

            {/* Úlohy: čo sa v tíme rozbehlo a čo dobehlo */}
            <Panel>
                <PanelHead
                    title={t('pages.dashboard.tasks.title')}
                    meta={t('pages.dashboard.tasks.subtitle')}
                />
                <TasksOverview
                    data={tasksOverview}
                    loading={tasksLoading && !loadedOnce.current}
                    language={language}
                    t={t}
                />
            </Panel>
        </Box>
    );
};

export default BoardUser;
